// Settings come from data- attributes on the script tag, then from an optional
// window.AdidoBugs object set before the tag (which wins, and is the only way to pass
// functions such as sanitisers or regular expressions).

/** Header values that are always replaced with [redacted]. */
export const CREDENTIAL_HEADERS = ['authorization', 'proxy-authorization', 'cookie', 'set-cookie'];

export const DEFAULT_CONFIG = Object.freeze({
  /** Identifies the site in reports. Required. */
  site: null,
  /** Where reports are POSTed. Defaults to /api/reports on the script's own origin. */
  endpoint: null,
  /** Base URL for replay.js and recorder.js. Defaults to the loader's own version folder. */
  assets: null,
  /** Record the DOM replay from page load, so reports include the time before the click. */
  replay: true,
  /** Show the "Report a problem" button. */
  button: true,
  /** 'bottom-right' or 'bottom-left'. */
  position: 'bottom-right',
  /** Accent colour for the button and panel. */
  accent: '#1d4ed8',
  /** Video length cap in seconds (10 to 600). */
  videoMax: 300,
  /** How much replay to keep, in seconds. */
  replayWindow: 120,
  /** How long to keep console, network and other log entries, in seconds. */
  logWindow: 600,
  /** Most entries kept per group (console, errors, network, resources, actions). */
  maxEntries: 500,
  /** Longest request or response body kept, in characters. */
  maxBodyLength: 100000,
  /** Longest single console argument kept, in characters. */
  maxArgLength: 1000,
  /** Mask every input value in replay and click capture. Passwords are always masked. */
  maskInputs: false,
  /** Extra CSS selector for elements to leave out of the replay entirely. */
  blockSelector: null,
  /** CSS selector for elements whose text is masked in the replay. */
  maskTextSelector: null,
  /** Extra header names to redact, on top of CREDENTIAL_HEADERS. */
  redactHeaders: [],
  /** JSON or form body keys whose values are redacted. */
  redactKeys: [],
  /** URL query parameters whose values are redacted. */
  redactParams: [],
  /** Paths (strings are prefixes, or RegExps) where the script does nothing at all. */
  excludePaths: [],
  /** Extra URLs (string prefixes or RegExps) that are never captured. */
  ignoreUrls: [],
  /**
   * Optional functions that clean entries before they are stored. Each receives the
   * entry detail and returns it (changed or not), or null to drop the entry.
   * Keys: network, websocket, console.
   */
  sanitisers: {},
  /** Turn individual capture hooks off. */
  capture: {
    console: true,
    errors: true,
    network: true,
    websocket: true,
    resources: true,
    csp: true,
    actions: true,
    navigation: true,
  },
});

const FALSE_VALUES = new Set(['false', '0', 'off', 'no']);

function parseBool(value, fallback) {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'boolean') return value;
  return !FALSE_VALUES.has(String(value).trim().toLowerCase());
}

function parseNumber(value, fallback) {
  if (value === undefined || value === null || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function asArray(value) {
  if (value === undefined || value === null) return [];
  return Array.isArray(value) ? value : [value];
}

function splitList(value) {
  if (!value) return [];
  return String(value)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * @param {{ dataset?: DOMStringMap | Record<string, string>, src?: string, windowConfig?: object }} input
 */
export function readConfig({ dataset = {}, src = '', windowConfig = {} } = {}) {
  const fromData = {
    site: dataset.site,
    endpoint: dataset.endpoint,
    assets: dataset.assets,
    replay: dataset.replay,
    button: dataset.button,
    position: dataset.position,
    accent: dataset.accent,
    videoMax: dataset.videoMax,
    maskInputs: dataset.maskInputs,
    blockSelector: dataset.blockSelector,
    maskTextSelector: dataset.maskTextSelector,
    redactHeaders: splitList(dataset.redactHeaders),
    redactKeys: splitList(dataset.redactKeys),
    redactParams: splitList(dataset.redactParams),
    excludePaths: splitList(dataset.excludePaths),
  };
  const win = windowConfig && typeof windowConfig === 'object' ? windowConfig : {};
  const pick = (key) => (win[key] !== undefined ? win[key] : fromData[key]);

  const config = {
    ...DEFAULT_CONFIG,
    site: pick('site') || null,
    endpoint: pick('endpoint') || null,
    assets: pick('assets') || null,
    replay: parseBool(pick('replay'), DEFAULT_CONFIG.replay),
    button: parseBool(pick('button'), DEFAULT_CONFIG.button),
    position: pick('position') === 'bottom-left' ? 'bottom-left' : 'bottom-right',
    accent: pick('accent') || DEFAULT_CONFIG.accent,
    videoMax: clamp(parseNumber(pick('videoMax'), DEFAULT_CONFIG.videoMax), 10, 600),
    replayWindow: clamp(parseNumber(win.replayWindow, DEFAULT_CONFIG.replayWindow), 10, 600),
    logWindow: clamp(parseNumber(win.logWindow, DEFAULT_CONFIG.logWindow), 30, 3600),
    maxEntries: clamp(parseNumber(win.maxEntries, DEFAULT_CONFIG.maxEntries), 50, 5000),
    maxBodyLength: clamp(parseNumber(win.maxBodyLength, DEFAULT_CONFIG.maxBodyLength), 0, 1000000),
    maxArgLength: clamp(parseNumber(win.maxArgLength, DEFAULT_CONFIG.maxArgLength), 50, 100000),
    maskInputs: parseBool(pick('maskInputs'), DEFAULT_CONFIG.maskInputs),
    blockSelector: pick('blockSelector') || null,
    maskTextSelector: pick('maskTextSelector') || null,
    redactHeaders: [...fromData.redactHeaders, ...asArray(win.redactHeaders)],
    redactKeys: [...fromData.redactKeys, ...asArray(win.redactKeys)],
    redactParams: [...fromData.redactParams, ...asArray(win.redactParams)],
    excludePaths: [...fromData.excludePaths, ...asArray(win.excludePaths)],
    ignoreUrls: asArray(win.ignoreUrls),
    sanitisers: win.sanitisers && typeof win.sanitisers === 'object' ? { ...win.sanitisers } : {},
    capture: { ...DEFAULT_CONFIG.capture, ...(win.capture || {}) },
  };

  if (!config.endpoint && src) {
    try {
      config.endpoint = new URL('/api/reports', src).href;
    } catch {
      // leave null; the panel explains that reports can't be sent
    }
  }
  if (config.endpoint) config.endpoint = config.endpoint.replace(/\/+$/, '');
  return config;
}

/** True when the current path matches one of config.excludePaths. */
export function isExcluded(config, pathname) {
  return config.excludePaths.some((rule) => {
    if (rule instanceof RegExp) return rule.test(pathname);
    return typeof rule === 'string' && rule !== '' && pathname.startsWith(rule);
  });
}

/** Builds a matcher for URLs we must never capture (our own endpoint and assets, plus ignoreUrls). */
export function createUrlMatcher(prefixes, rules) {
  const cleanPrefixes = prefixes.filter(Boolean);
  return (url) => {
    if (!url) return false;
    const str = String(url);
    if (cleanPrefixes.some((p) => str.startsWith(p))) return true;
    return rules.some((rule) => {
      if (rule instanceof RegExp) return rule.test(str);
      return typeof rule === 'string' && rule !== '' && str.startsWith(rule);
    });
  };
}
